import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import paymentService from '../services/paymentService';
import './Payment.css';
import whatsAppService from '../../../services/whatsAppService';
import { clearEnrollmentData } from '../../../redux/slices/classSlice';
import { getPaymentError, PROFILE_PATH } from '../../../utils/paymentErrors';

const Payment = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedClass, enrollmentData } = useSelector((state) => state.class);
  const { user } = useSelector((state) => state.auth);
  
  const [paymentMethod, setPaymentMethod] = useState('credit');
  const [processing, setProcessing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [autoCloseTimer, setAutoCloseTimer] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isAddressMismatch, setIsAddressMismatch] = useState(false);
  const [errorReferenceCode, setErrorReferenceCode] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
  const [formData, setFormData] = useState({
    // Credit Card fields
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: `${user?.User_First_Name || user?.user_First_Name || ''} ${user?.User_Last_Name || user?.user_Last_Name || ''}`.trim(),
    // Billing ZIP - pre-filled from profile, editable so it can match the card statement
    zipCode: String(user?.User_Zipcode || user?.user_Zipcode || ''),
    // ACH fields
    accountHolderName: '',
    routingNumber: '',
    accountNumber: '',
    accountType: 'checking',
    bankName: '',
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [lookingUpBank, setLookingUpBank] = useState(false);
  
  // Get data from Redux - use useMemo to prevent re-renders
  const classId = enrollmentData?.classId;
  const selectedStudentIds = enrollmentData?.selectedStudentIds || [];
  const students = useMemo(() => enrollmentData?.students || [], [enrollmentData?.students]);
  const totalFee = enrollmentData?.totalFee || 0;
  
  useEffect(() => {
    // Redirect if no enrollment data in Redux
    if (!enrollmentData || !students || students.length === 0) {
      navigate('/class/enroll-student');
    }
  }, [enrollmentData, students, navigate]);

  // Auto-close success modal after 20 seconds
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false);
        dispatch(clearEnrollmentData());
        navigate('/');
      }, 20000); // 20 seconds
      
      setAutoCloseTimer(timer);
      
      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [showSuccessModal, dispatch, navigate]);
  
  // Load and render Google reCAPTCHA v2
  useEffect(() => {
    const ensureScript = () => {
      return new Promise((resolve) => {
        const w = window;
        if (w.grecaptcha) return resolve();
        const existing = document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]');
        if (existing) {
          existing.addEventListener('load', () => resolve());
          return;
        }
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        document.body.appendChild(s);
      });
    };

    const renderWidget = () => {
      const w = window;
      const el = document.getElementById('recaptcha-container-payment');
      if (!el) return;
      if (recaptchaWidgetId !== null) return;

      const pollUntilReady = () => {
        const api = (w && w.grecaptcha) ? w.grecaptcha : null;
        if (!api) {
          setTimeout(pollUntilReady, 150);
          return;
        }
        const doRender = () => {
          if (recaptchaWidgetId !== null) return;
          if (typeof api.render !== 'function') {
            setTimeout(doRender, 150);
            return;
          }
          try {
            const id = api.render(el, {
              sitekey: RECAPTCHA_SITE_KEY,
              callback: (token) => setRecaptchaToken(token),
              'expired-callback': () => setRecaptchaToken(''),
              'error-callback': () => setRecaptchaToken(''),
            });
            setRecaptchaWidgetId(id);
          } catch (e) {
            console.warn('reCAPTCHA render failed, retrying...', e);
            setTimeout(doRender, 200);
          }
        };

        if (typeof api.ready === 'function') {
          api.ready(doRender);
        } else {
          setTimeout(doRender, 0);
        }
      };

      pollUntilReady();
    };

    let mounted = true;
    ensureScript().then(() => {
      if (!mounted) return;
      setTimeout(renderWidget, 0);
    });
    return () => {
      mounted = false;
    };
  }, [RECAPTCHA_SITE_KEY, recaptchaWidgetId]);
  
  const handleBack = () => {
    navigate(-1);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    // Format credit card number (remove non-digits and add spaces)
    if (name === 'cardNumber') {
      const digits = value.replace(/\D/g, '');
      formattedValue = digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
    }
    
    // Format expiry date (MM/YY)
    if (name === 'expiryDate') {
      const digits = value.replace(/\D/g, '');
      if (digits.length <= 2) {
        formattedValue = digits;
      } else {
        formattedValue = digits.slice(0, 2) + '/' + digits.slice(2, 4);
      }
    }
    
    // Format CVV (digits only)
    if (name === 'cvv') {
      formattedValue = value.replace(/\D/g, '');
    }
    
    // Format routing number (digits only, max 9)
    if (name === 'routingNumber') {
      formattedValue = value.replace(/\D/g, '').slice(0, 9);
    }
    
    // Format account number (digits only)
    if (name === 'accountNumber') {
      formattedValue = value.replace(/\D/g, '');
    }
    
    // Format ZIP code (digits and hyphen only, max 10 for ZIP+4)
    if (name === 'zipCode') {
      formattedValue = value.replace(/[^\d-]/g, '').slice(0, 10);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
    
    // Lookup bank name when routing number is complete (9 digits)
    if (name === 'routingNumber' && formattedValue.length === 9) {
      lookupBankName(formattedValue);
    }
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    setValidationErrors({});
  };
  
  // Validation functions
  const validateZipCode = (errors) => {
    const zip = formData.zipCode.trim();
    if (!zip) errors.zipCode = 'Billing ZIP code is required';
    else if (!/^\d{5}(-\d{4})?$/.test(zip)) errors.zipCode = 'Enter a valid US ZIP code (e.g. 30096 or 30096-1234)';
    return errors;
  };

  const validateCreditCard = () => {
    const errors = {};
    
    // Validate card number (13-19 digits)
    const cardDigits = formData.cardNumber.replace(/\D/g, '');
    if (!cardDigits) {
      errors.cardNumber = 'Card number is required';
    } else if (cardDigits.length < 13 || cardDigits.length > 19) {
      errors.cardNumber = 'Card number must be 13-19 digits';
    }
    
    // Validate expiry date (MM/YY format)
    if (!formData.expiryDate) {
      errors.expiryDate = 'Expiry date is required';
    } else {
      const expiryMatch = formData.expiryDate.match(/^(\d{2})\/(\d{2})$/);
      if (!expiryMatch) {
        errors.expiryDate = 'Use MM/YY format';
      } else {
        const month = parseInt(expiryMatch[1], 10);
        const year = parseInt(expiryMatch[2], 10);
        if (month < 1 || month > 12) {
          errors.expiryDate = 'Invalid month (01-12)';
        } else {
          const now = new Date();
          const currentYear = now.getFullYear() % 100;
          const currentMonth = now.getMonth() + 1;
          if (year < currentYear || (year === currentYear && month < currentMonth)) {
            errors.expiryDate = 'Card has expired';
          }
        }
      }
    }
    
    // Validate CVV (3-4 digits)
    if (!formData.cvv) {
      errors.cvv = 'CVV is required';
    } else if (formData.cvv.length < 3 || formData.cvv.length > 4) {
      errors.cvv = 'CVV must be 3-4 digits';
    }
    
    return errors;
  };
  
  const lookupBankName = async (routingNumber) => {
    setLookingUpBank(true);
    try {
      console.log('Looking up bank name for routing number:', routingNumber);
      
      // Call backend API endpoint to lookup routing number
      // Backend will handle the external API call without CORS issues
      const API_BASE_URL = process.env.REACT_APP_YK_API_BASE || 'http://localhost:5000/ykrestapi';
      
      try {
        const response = await fetch(`${API_BASE_URL}/CommonChannel/GetBankNameByRoutingNumber?routingNumber=${routingNumber}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          }
        });
        
        console.log('Backend API response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Backend API response data:', data);
          
          // Extract bank name from response
          const bankName = data.bankName || data.name || data.institutionName;
          
          if (bankName && bankName !== 'string' && bankName.trim().length > 0) {
            console.log('Bank name found:', bankName);
            setFormData(prev => ({
              ...prev,
              bankName: bankName.trim()
            }));
            setLookingUpBank(false);
            return;
          }
        } else {
          console.warn('Backend API returned error status:', response.status);
        }
      } catch (apiError) {
        console.error('Backend API call failed:', apiError);
      }
      
      // If API fails, leave empty for manual entry
      console.warn('Could not retrieve bank name, please enter manually');
      setFormData(prev => ({
        ...prev,
        bankName: '' // Leave empty so user must enter
      }));
      
    } catch (error) {
      console.error('Bank lookup error:', error);
      setFormData(prev => ({
        ...prev,
        bankName: ''
      }));
    } finally {
      setLookingUpBank(false);
    }
  };
  
  const validateACH = () => {
    const errors = {};
    
    // Validate account holder name
    if (!formData.accountHolderName || formData.accountHolderName.trim().length < 2) {
      errors.accountHolderName = 'Account holder name is required (min 2 characters)';
    }
    
    // Validate routing number (exactly 9 digits)
    if (!formData.routingNumber) {
      errors.routingNumber = 'Routing number is required';
    } else if (formData.routingNumber.length !== 9) {
      errors.routingNumber = 'Routing number must be exactly 9 digits';
    }
    
    // Validate account number (5-17 digits)
    if (!formData.accountNumber) {
      errors.accountNumber = 'Account number is required';
    } else if (formData.accountNumber.length < 5 || formData.accountNumber.length > 17) {
      errors.accountNumber = 'Account number must be 5-17 digits';
    }
    
    // Validate bank name
    if (!formData.bankName || formData.bankName.trim().length < 2) {
      errors.bankName = 'Bank name is required';
    }
    
    return errors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate reCAPTCHA first
    if (!recaptchaToken) {
      setValidationErrors({ recaptcha: 'Please complete the reCAPTCHA verification' });
      // Scroll to reCAPTCHA
      document.getElementById('recaptcha-container-payment')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    // Validate form based on payment method
    let errors = {};
    if (paymentMethod === 'credit') {
      errors = validateCreditCard();
    } else {
      errors = validateACH();
    }
    validateZipCode(errors);
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Scroll to first error field
      const firstErrorField = Object.keys(errors)[0];
      document.getElementsByName(firstErrorField)[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    setProcessing(true);
    
    try {
      // Debug: Log user data to see what's available
      console.log('User data from Redux:', user);
      console.log('User address fields:', {
        User_Address_Line1: user?.User_Address_Line1,
        User_Address_Line2: user?.User_Address_Line2,
        User_City: user?.User_City,
        User_State: user?.User_State,
        User_Zipcode: user?.User_Zipcode,
        User_Country: user?.User_Country
      });
      
      // Get state and country values from user profile
      const userStateId = user?.State_Id || user?.state_Id || user?.stateId || user?.User_State || user?.user_State || '';
      const rawCountry = user?.Country_Id || user?.country_Id || user?.countryId || user?.User_Country || user?.user_Country || '1';
      const userCountryId = parseInt(rawCountry) ? String(parseInt(rawCountry)) : '1';
      
      // Fetch state and country codes from database
      const API_BASE_URL = process.env.REACT_APP_YK_API_BASE || 'http://localhost:5000/ykrestapi';
      
      let mappedState = user?.StateSortCode || user?.stateSortCode || '';
      let mappedCountry = user?.CountrySortCode || user?.countrySortCode || '';
      
      // If sort codes not in user profile, fetch from database
      if (!mappedState && userStateId && userCountryId) {
        try {
          const statesResponse = await fetch(`${API_BASE_URL}/CommonChannel/GetStatesByCountry?countryId=${userCountryId}`);
          if (statesResponse.ok) {
            const states = await statesResponse.json();
            console.log('States API response:', states);
            const sid = parseInt(userStateId);
            const state = states.find(s =>
              s.UserStateId === sid || s.userStateId === sid ||
              s.state_Id === sid || s.stateId === sid
            );
            console.log('Found state:', state);
            mappedState = state?.ShortCode || state?.shortCode || state?.sortCode || state?.SortCode || '';
          }
        } catch (error) {
          console.error('Error fetching state code:', error);
        }
      }
      
      if (!mappedCountry && userCountryId) {
        try {
          const countriesResponse = await fetch(`${API_BASE_URL}/CommonChannel/GetCountries`);
          if (countriesResponse.ok) {
            const countries = await countriesResponse.json();
            console.log('Countries API response:', countries);
            const country = countries.find(c => c.country_Id === parseInt(userCountryId) || c.countryId === parseInt(userCountryId));
            console.log('Found country:', country);
            mappedCountry = country?.sortCode || country?.SortCode || country?.country_short || '';
          }
        } catch (error) {
          console.error('Error fetching country code:', error);
        }
      }
      
      // Default values if still empty
      mappedState = mappedState || 'GA';
      mappedCountry = mappedCountry || 'US';
      
      console.log('State mapping:', { original: userStateId, mapped: mappedState });
      console.log('Country mapping:', { original: userCountryId, mapped: mappedCountry });

      // Prepare payment data with user's billing address
      const paymentData = {
        amount: totalFee,
        classId: classId,
        className: selectedClass?.class_Name || '',
        categoryType: selectedClass?.category_Type || 'RMS',
        entity: selectedClass?.category_Type || 'RMS',
        studentIds: selectedStudentIds,
        numberOfStudents: students?.length || 0,
        userId: user?.User_Id || user?.user_Id || user?.userId,
        userEmail: user?.User_Email || user?.user_Email || user?.email,
        userName: `${user?.User_First_Name || user?.user_First_Name || user?.firstName || ''} ${user?.User_Last_Name || user?.user_Last_Name || user?.lastName || ''}`,
        userPhone: user?.User_Phone || user?.User_Cell_Phone_No || user?.user_Phone || '',
        userAddress: user?.User_Address_Line1 || user?.user_Address_Line1 || '',
        streetAddress1: user?.User_Address_Line1 || user?.user_Address_Line1 || '',
        streetAddress2: user?.User_Address_Line2 || user?.user_Address_Line2 || '',
        city: user?.User_City || user?.user_City || '',
        state: mappedState,
        stateCode: mappedState,
        zipCode: formData.zipCode.trim(),
        postalCode: formData.zipCode.trim(),
        country: mappedCountry,
        paymentMethod: paymentMethod,
        transactionDate: new Date().toISOString()
      };
      
      console.log('Payment data being sent:', paymentData);
      
      // Prepare payment details based on method
      let paymentDetails = {
        ...paymentData,
        paymentType: paymentMethod === 'credit' ? 'CreditCard' : 'ACH'
      };
      
      if (paymentMethod === 'credit') {
        // Credit Card Payment - Remove spaces from card number
        paymentDetails = {
          ...paymentDetails,
          cardNumber: formData.cardNumber.replace(/\s/g, ''),
          expiryDate: formData.expiryDate,
          cvv: formData.cvv,
          cardholderName: formData.cardholderName
        };
      } else {
        // ACH Payment
        paymentDetails = {
          ...paymentDetails,
          accountHolderName: formData.accountHolderName,
          routingNumber: formData.routingNumber,
          accountNumber: formData.accountNumber,
          accountType: formData.accountType,
          bankName: formData.bankName || 'Financial Institution'
        };
      }
      
      console.log('Payment details with form data:', paymentDetails);
      
      // Process one-time payment via Authorize.Net
      const paymentResponse = await paymentService.processOneTimePayment(paymentDetails);
      
      console.log('Payment Response:', paymentResponse);
      
      // Check if payment was successful - Authorize.Net returns responseCode "1" for approved
      const isApproved = paymentResponse?.createTransactionResponse?.transactionResponse?.responseCode === "1" ||
                         paymentResponse?.createTransactionResponse?.messages?.resultCode === "Ok";
      
      if (paymentResponse && isApproved) {
        const transactionId = paymentResponse?.createTransactionResponse?.transactionResponse?.transId || '';
        const userName = `${user?.User_First_Name || user?.user_First_Name || user?.firstName || ''} ${user?.User_Last_Name || user?.user_Last_Name || user?.lastName || ''}`;
        const userId = user?.User_Id || user?.user_Id || user?.userId;

        // Step 1: Create payment record to get Payment_Details_Id
        const userFirstName = user?.User_First_Name || user?.user_First_Name || user?.firstName || '';
        const userLastName = user?.User_Last_Name || user?.user_Last_Name || user?.lastName || '';
        const userEmail = user?.User_Email || user?.user_Email || user?.email || '';
        const userPhone = user?.User_Phone || user?.User_Cell_Phone_No || user?.user_Phone || user?.phone || '';

        const categoryType = selectedClass?.category_Type || user?.category_Type || 'RMS';
        
        const paymentRecordData = {
          user_payment_detail_id: userId,
          payment_type_method: 'authnet',
          payment_instruction: 'Class enrollment payment',
          createdby: userName,
          created: new Date().toISOString(),
          modifiedby: userName,
          modified: new Date().toISOString(),
          status: 'Y',
          user_id: userId,
          card_holder_name: userName,
          billing_address_line1: user?.User_Address_Line1 || user?.user_Address_Line1 || '',
          billing_address_line2: user?.User_Address_Line2 || user?.user_Address_Line2 || '',
          city: user?.User_City || user?.user_City || '',
          state: String(user?.User_State || user?.user_State || ''),
          country: String(user?.User_Country || user?.user_Country || ''),
          zipcode: formData.zipCode.trim(),
          class_id: parseInt(classId) || 0,
          grandtotal: totalFee,
          totalamount: totalFee,
          discountamount: 0.00,
          payment_status_message: 'approved',
          // Required fields
          User_First_Name: userFirstName,
          User_Last_Name: userLastName,
          User_Email: userEmail,
          User_Phone: userPhone,
          Categpry_RMS_OR_HYC: categoryType
        };
        
        console.log('Creating payment record:', paymentRecordData);
        let paymentRecord = null;
        let paymentDetailsId = null;
        
        try {
          paymentRecord = await paymentService.createPaymentRecord(paymentRecordData);
          console.log('Payment record created:', paymentRecord);
          paymentDetailsId = paymentRecord?.Payment_Details_Id || paymentRecord?.payment_Details_Id;
        } catch (postPayError) {
          console.error('PostPayment record failed (card was already charged):', postPayError);
        }
        
        if (!paymentDetailsId) {
          // Payment was charged by AuthNet but enrollment record creation failed
          // Show success modal so user knows card was charged, with contact-support note
          setPaymentResult({
            transactionId: transactionId,
            className: selectedClass?.class_Name || '',
            numberOfStudents: students?.length || 0,
            students: students,
            totalAmount: totalFee,
            transactionDate: new Date().toLocaleString(),
            receiptUrl: null,
            paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
            stepStatus: {
              payment: { success: true, message: 'Payment charged successfully' },
              enrollment: { success: false, message: `Enrollment recording failed. Please contact support with Transaction ID: ${transactionId}` },
              email: { success: false, message: 'Confirmation email not sent' }
            }
          });
          setShowSuccessModal(true);
          return;
        }
        
        // Track step statuses
        const stepStatus = {
          payment: { success: true, message: 'Payment processed successfully' },
          enrollment: { success: true, message: '', failedStudents: [] },
          email: { success: true, message: '' }
        };
        
        // Step 2: Create enrollment records for each selected student with payment_details_id
        console.log('Creating enrollments for students after payment:', students);
        const enrollmentResults = [];
        
        for (let i = 0; i < students.length; i++) {
          const student = students[i];
          try {
            const enrollmentData = {
              payment_details_id: parseInt(paymentDetailsId),
              student_id: parseInt(student.student_Id || student.studentId),
              class_id: parseInt(classId),
              createdby: userName,
              created: new Date().toISOString(),
              modifiedby: userName,
              modified: new Date().toISOString(),
              student_name: `${student.student_First_Name || student.firstName} ${student.student_Last_Name || student.lastName}`,
              status: 'Y'
            };
            
            console.log(`Creating enrollment ${i + 1}/${students.length} for student:`, enrollmentData);
            const result = await paymentService.createClassEnrollmentAndOrder(enrollmentData);
            console.log(`Enrollment ${i + 1} created successfully:`, result);
            enrollmentResults.push({ student, success: true });
          } catch (enrollError) {
            console.error(`Failed to create enrollment for student ${student.student_First_Name}:`, enrollError);
            stepStatus.enrollment.success = false;
            stepStatus.enrollment.failedStudents.push(`${student.student_First_Name || student.firstName} ${student.student_Last_Name || student.lastName}`);
            enrollmentResults.push({ student, success: false, error: enrollError.message });
          }
        }
        
        if (stepStatus.enrollment.success) {
          stepStatus.enrollment.message = 'All students enrolled successfully';
          console.log('All enrollments created successfully');
        } else {
          stepStatus.enrollment.message = `Failed to enroll: ${stepStatus.enrollment.failedStudents.join(', ')}`;
          console.warn('Some enrollments failed:', stepStatus.enrollment.failedStudents);
        }
        
        // Step 3 & 4: Email + WhatsApp in PARALLEL — neither failure blocks the other
        const classDateFrom = selectedClass?.class_Date_From ? new Date(selectedClass.class_Date_From).toLocaleDateString() : '';
        const classDateTo = selectedClass?.class_Date_To ? new Date(selectedClass.class_Date_To).toLocaleDateString() : '';
        const classDateRange = classDateFrom && classDateTo ? `${classDateFrom} - ${classDateTo}` :
                               classDateFrom ? classDateFrom : 'To be announced';

        const addressLine1 = user?.User_Address_Line1 || user?.user_Address_Line1 || '';
        const addressLine2 = user?.User_Address_Line2 || user?.user_Address_Line2 || '';
        const city = user?.User_City || user?.user_City || '';
        const stateCode = mappedState || '';
        const countryCode = mappedCountry || 'US';
        const zipcode = formData.zipCode.trim();
        const billingAddress = [addressLine1, addressLine2, city, stateCode, zipcode, countryCode]
          .filter(part => part && part.trim()).join(', ');

        const emailData = {
          toEmail: user?.User_Email || user?.user_Email || user?.email,
          userName: userName,
          userPhone: userPhone,
          className: selectedClass?.class_Name || '',
          classDateRange: classDateRange,
          numberOfStudents: students?.length || 0,
          students: students,
          totalAmount: totalFee,
          transactionId: transactionId,
          paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
          transactionDate: new Date().toLocaleString(),
          billingAddress: billingAddress || 'Not provided'
        };

        const waPhone = (userPhone || '').replace(/\D/g, '');
        const waPayload = {
          customerName: userName,
          phoneNumbers: waPhone ? [waPhone] : [],
          paymentId: transactionId,
          amount: String(totalFee),
          currency: 'USD',
          orderId: String(classId || ''),
          serviceName: 'Class',
          appName: 'Class',
          templateName: 'YKClassPayment',
          orgId: 3,
          className: selectedClass?.class_Name || '',
          classDate: classDateRange,
          studentCount: students?.length || 0
        };

        console.log('Sending email + WhatsApp in parallel...');
        const [emailResult, waResult] = await Promise.allSettled([
          paymentService.sendPaymentConfirmationEmail(emailData),
          whatsAppService.sendPaymentConfirmation(waPayload)
        ]);

        // Handle email result
        let receiptUrl = null;
        if (emailResult.status === 'fulfilled') {
          stepStatus.email.message = 'Confirmation email sent successfully';
          receiptUrl = emailResult.value?.receiptUrl || emailResult.value || null;
          console.log('Email sent successfully. Receipt URL:', receiptUrl);
        } else {
          stepStatus.email.success = false;
          stepStatus.email.message = 'Failed to send confirmation email';
          console.error('Email failed:', emailResult.reason);
        }

        // Handle WhatsApp result
        if (waResult.status === 'fulfilled') {
          console.log('[WhatsApp] Sent:', waResult.value);
        } else {
          console.warn('[WhatsApp] Notification failed (email was unaffected):', waResult.reason);
        }

        setPaymentResult({
          transactionId: transactionId,
          className: selectedClass?.class_Name || '',
          numberOfStudents: students?.length || 0,
          students: students,
          totalAmount: totalFee,
          transactionDate: new Date().toLocaleString(),
          receiptUrl: receiptUrl,
          paymentMethod: paymentMethod === 'credit' ? 'Credit Card' : 'ACH',
          stepStatus: stepStatus
        });
        setShowSuccessModal(true);

        // Reset reCAPTCHA after successful payment
        try {
          const w = window;
          if (recaptchaWidgetId !== null && w.grecaptcha) {
            w.grecaptcha.reset(recaptchaWidgetId);
            setRecaptchaToken('');
          }
        } catch (e) {
          console.warn('Failed to reset reCAPTCHA:', e);
        }
      } else {
        // Payment was not approved - normalise the Authorize.Net error and surface it via the catch block
        const paymentError = getPaymentError(paymentResponse);
        const declined = new Error(paymentError.message);
        declined.paymentError = paymentError;
        throw declined;
      }
    } catch (error) {
      console.error('Payment error:', error);
      
      const { message: displayMessage, isAddressMismatch: addressMismatch, referenceCode, transactionId } =
        error?.paymentError ?? getPaymentError(error);
      if (transactionId) console.warn('Declined Authorize.Net transaction:', transactionId);
      
      // Show error modal instead of alert
      setErrorMessage(displayMessage);
      setIsAddressMismatch(addressMismatch);
      setErrorReferenceCode(referenceCode);
      setShowErrorModal(true);
      
      // Reset reCAPTCHA on error
      try {
        const w = window;
        if (recaptchaWidgetId !== null && w.grecaptcha) {
          w.grecaptcha.reset(recaptchaWidgetId);
          setRecaptchaToken('');
        }
      } catch (e) {
        console.warn('Failed to reset reCAPTCHA:', e);
      }
    } finally {
      setProcessing(false);
    }
  };
  
  if (!students || students.length === 0) {
    return null;
  }
  
  return (
    <div className="class-payment-page min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">
                Payment Details
              </h1>
              <p className="text-gray-600 text-sm">Complete your class enrollment payment</p>
            </div>
            
            <button
              onClick={handleBack}
              className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-full transition shadow-md hover:shadow-lg flex items-center gap-2"
            >
              ← Back
            </button>
          </div>
        </div>
        
        {/* Two Column Layout - Order Summary (Left) and Payment Form (Right) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Order Summary (Sticky) */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6 lg:sticky lg:top-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Order Summary</h2>
              
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-gray-700 text-sm">Class Name:</span>
                  <span className="text-gray-800 font-medium text-right text-sm max-w-[60%]">
                    {selectedClass?.class_Name || 'Yoga And Pranayam - Single Yearly - 2026'}
                  </span>
                </div>
                
                {/* Students List */}
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="text-gray-700 text-sm font-semibold">Students ({students?.length || 0})</span>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {students.map((student, index) => {
                      const firstName = student.student_First_Name || student.firstName || '';
                      const lastName = student.student_Last_Name || student.lastName || '';
                      const gender = (student.student_Gender || student.gender || '').toLowerCase();
                      const genderIcon = gender === 'male' || gender === 'm' ? '👦' : gender === 'female' || gender === 'f' ? '👧' : '👤';
                      const feePerStudent = selectedClass?.class_Fee || 240;
                      
                      return (
                        <div key={index} className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg">
                          <span className="text-lg">{genderIcon}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-800 font-semibold text-xs truncate">{firstName} {lastName}</p>
                            <p className="text-gray-500 text-xs">${feePerStudent.toFixed(2)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="text-gray-800 font-bold">Total:</span>
                  <span className="text-green-600 font-bold text-xl">${totalFee?.toFixed(2) || '0.00'}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right Column - Payment Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit}>
              <div className="bg-white rounded-xl shadow-lg p-6">
                {/* Payment Method Tabs */}
                <div className="mb-4">
                  <h2 className="text-lg font-bold text-gray-800 mb-3">Select Payment Method</h2>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => handlePaymentMethodChange('credit')}
                      className={`flex-1 py-2.5 px-4 rounded-lg font-semibold transition text-sm ${
                        paymentMethod === 'credit'
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      💳 Credit/Debit Card
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePaymentMethodChange('ach')}
                      className={`flex-1 py-2.5 px-4 rounded-lg font-semibold transition text-sm ${
                        paymentMethod === 'ach'
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      🏦 Bank Account (ACH)
                    </button>
                  </div>
                </div>
                
                {/* Payment Details */}
                <div className="space-y-4">
              {paymentMethod === 'credit' ? (
                <>
                    {/* Credit Card Form */}
                    <div>
                      <h3 className="text-base font-semibold text-gray-800 mb-3">Card Information</h3>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            Card Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="cardNumber"
                            value={formData.cardNumber}
                            onChange={handleInputChange}
                            placeholder="1234 5678 9012 3456"
                            maxLength="19"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                              validationErrors.cardNumber
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-blue-500'
                            }`}
                            required
                          />
                          {validationErrors.cardNumber && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.cardNumber}</p>
                          )}
                        </div>
                      
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                              Expiry Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="expiryDate"
                              value={formData.expiryDate}
                              onChange={handleInputChange}
                              placeholder="MM/YY"
                              maxLength="5"
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                                validationErrors.expiryDate
                                  ? 'border-red-500 focus:ring-red-500'
                                  : 'border-gray-300 focus:ring-blue-500'
                              }`}
                              required
                            />
                            {validationErrors.expiryDate && (
                              <p className="text-red-500 text-xs mt-1">{validationErrors.expiryDate}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                              CVV <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="cvv"
                              value={formData.cvv}
                              onChange={handleInputChange}
                              placeholder="123"
                              maxLength="4"
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                                validationErrors.cvv
                                  ? 'border-red-500 focus:ring-red-500'
                                  : 'border-gray-300 focus:ring-blue-500'
                              }`}
                              required
                            />
                            {validationErrors.cvv && (
                              <p className="text-red-500 text-xs mt-1">{validationErrors.cvv}</p>
                            )}
                          </div>
                        </div>
                      
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            Cardholder Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="cardholderName"
                            value={formData.cardholderName}
                            onChange={handleInputChange}
                            placeholder="Name as it appears on card"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                              validationErrors.cardholderName
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-blue-500'
                            }`}
                            required
                          />
                          {validationErrors.cardholderName && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.cardholderName}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">✏️ You can edit this if the card is in a different name</p>
                        </div>

                        {/* Billing ZIP Code */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            Billing ZIP Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            name="zipCode"
                            value={formData.zipCode}
                            onChange={handleInputChange}
                            placeholder="e.g. 30096 or 30096-1234"
                            maxLength="10"
                            autoComplete="postal-code"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                              validationErrors.zipCode
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-blue-500'
                            }`}
                            required
                          />
                          {validationErrors.zipCode && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.zipCode}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">✏️ Pre-filled from your profile — must match the ZIP on your card statement</p>
                        </div>
                      </div>
                    </div>
                </>
              ) : (
                <>
                    {/* ACH Form */}
                    <div>
                      <h3 className="text-base font-semibold text-gray-800 mb-3">Bank Account Information</h3>
                      
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            Account Holder Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="accountHolderName"
                            value={formData.accountHolderName}
                            onChange={handleInputChange}
                            placeholder="John Doe"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                              validationErrors.accountHolderName
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-blue-500'
                            }`}
                            required
                          />
                          {validationErrors.accountHolderName && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.accountHolderName}</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            Routing Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="routingNumber"
                            value={formData.routingNumber}
                            onChange={handleInputChange}
                            placeholder="123456789"
                            maxLength="9"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                              validationErrors.routingNumber
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-blue-500'
                            }`}
                            required
                          />
                          {validationErrors.routingNumber && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.routingNumber}</p>
                          )}
                          {lookingUpBank && (
                            <p className="text-blue-500 text-xs mt-1">🔍 Looking up bank name...</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            Bank Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="bankName"
                            value={formData.bankName}
                            onChange={handleInputChange}
                            placeholder="Enter your bank name"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                              validationErrors.bankName
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-blue-500'
                            }`}
                            required
                          />
                          {validationErrors.bankName && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.bankName}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">Will auto-fill from routing number, or enter manually</p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            Account Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="accountNumber"
                            value={formData.accountNumber}
                            onChange={handleInputChange}
                            placeholder="1234567890"
                            maxLength="17"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                              validationErrors.accountNumber
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-blue-500'
                            }`}
                            required
                          />
                          {validationErrors.accountNumber && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.accountNumber}</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            Account Type <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="accountType"
                            value={formData.accountType}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            required
                          >
                            <option value="checking">Checking</option>
                            <option value="savings">Savings</option>
                          </select>
                        </div>

                        {/* Billing ZIP Code */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            Billing ZIP Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            name="zipCode"
                            value={formData.zipCode}
                            onChange={handleInputChange}
                            placeholder="e.g. 30096 or 30096-1234"
                            maxLength="10"
                            autoComplete="postal-code"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent text-sm ${
                              validationErrors.zipCode
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-300 focus:ring-blue-500'
                            }`}
                            required
                          />
                          {validationErrors.zipCode && (
                            <p className="text-red-500 text-xs mt-1">{validationErrors.zipCode}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">✏️ Pre-filled from your profile — must match your bank's billing ZIP</p>
                        </div>
                      </div>
                    </div>
                </>
              )}
                </div>
                
                {/* reCAPTCHA */}
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <div className="mb-3">
                    <div id="recaptcha-container-payment" className={validationErrors.recaptcha ? 'ring-2 ring-red-500 rounded p-2' : ''}></div>
                    {validationErrors.recaptcha && (
                      <p className="text-red-500 text-sm mt-2 font-semibold">{validationErrors.recaptcha}</p>
                    )}
                  </div>
                </div>
                
                {/* Submit Buttons */}
                <div className="flex gap-3 pt-3">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2.5 px-4 rounded-lg transition text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className={`flex-1 font-semibold py-2.5 px-4 rounded-lg transition shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm ${
                      processing 
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {processing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Processing...
                      </>
                    ) : (
                      `Pay $${totalFee?.toFixed(2) || '0.00'}`
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Processing Modal */}
      {processing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center animate-slideInUp">
            {/* Animated Spinner */}
            <div className="mb-6 flex justify-center">
              <div className="relative flex items-center justify-center" style={{ width: 88, height: 88 }}>
                {/* Outer slow ring */}
                <div className="absolute rounded-full border-4 border-gray-200" style={{ width: 88, height: 88 }}></div>
                <div className="absolute rounded-full border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent" style={{ width: 88, height: 88, animation: 'spin 1.2s linear infinite' }}></div>
                {/* Middle ring - opposite direction */}
                <div className="absolute rounded-full border-4 border-gray-100" style={{ width: 64, height: 64 }}></div>
                <div className="absolute rounded-full border-4 border-b-orange-400 border-t-transparent border-r-transparent border-l-transparent" style={{ width: 64, height: 64, animation: 'spin 0.9s linear infinite reverse' }}></div>
                {/* Center pulsing dot */}
                <div className="absolute rounded-full bg-blue-500 animate-pulse" style={{ width: 16, height: 16 }}></div>
              </div>
            </div>
            
            {/* Processing Text */}
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing Payment</h2>
            <p className="text-gray-600 mb-4">Please wait while we process your payment...</p>
            
            {/* Progress Steps */}
            <div className="space-y-2 text-left bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <span className="text-sm text-gray-700">Processing payment transaction...</span>
              </div>
              <div className="flex items-center gap-2 opacity-50">
                <div className="w-5 h-5 rounded-full border-2 border-gray-300"></div>
                <span className="text-sm text-gray-500">Creating enrollment records...</span>
              </div>
              <div className="flex items-center gap-2 opacity-50">
                <div className="w-5 h-5 rounded-full border-2 border-gray-300"></div>
                <span className="text-sm text-gray-500">Sending confirmation email...</span>
              </div>
            </div>
            
            <p className="text-xs text-gray-500 mt-4">⚠️ Please do not close this window or press the back button</p>
          </div>
        </div>
      )}

      {/* Success Modal - Wide Design */}
      {showSuccessModal && paymentResult && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fadeIn" onClick={() => {
            if (autoCloseTimer) clearTimeout(autoCloseTimer);
            setShowSuccessModal(false);
            dispatch(clearEnrollmentData());
            navigate('/');
          }}>
            {/* Modal - Wide Design */}
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] flex flex-col animate-slideInUp" onClick={(e) => e.stopPropagation()}>
              {/* Header - Sleek Design */}
              <div className="relative bg-gradient-to-br from-green-500 via-emerald-600 to-teal-600 text-white p-5 text-center rounded-t-2xl overflow-hidden flex-shrink-0">
                {/* Close Button */}
                <button
                  onClick={() => {
                    if (autoCloseTimer) clearTimeout(autoCloseTimer);
                    setShowSuccessModal(false);
                    dispatch(clearEnrollmentData());
                    navigate('/');
                  }}
                  className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 z-10"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                {/* Decorative Background Elements */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/10 rounded-full -ml-10 -mb-10"></div>
                
                {/* Success Icon */}
                <div className="relative w-14 h-14 bg-white rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg transform hover:scale-110 transition-transform duration-300">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                
                {/* Title */}
                <h2 className="relative text-2xl font-extrabold mb-1 tracking-tight">Payment Successful!</h2>
                <p className="relative text-green-50 text-sm font-medium">Your class enrollment has been confirmed</p>
              </div>

              {/* Content - Scrollable with Two Columns */}
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                {/* Auto-close notification */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-center">
                  <p className="text-xs text-blue-700">✨ This window will automatically close in 20 seconds or click anywhere to close</p>
                </div>

                {/* Two Column Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Left Column */}
                  <div className="space-y-4">
                    {/* Process Steps Status */}
                    {paymentResult.stepStatus && (
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <h3 className="font-bold text-gray-800 mb-3 text-sm">Process Status</h3>
                    <div className="space-y-1.5">
                      {/* Payment Step */}
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${paymentResult.stepStatus.payment.success ? 'bg-green-500' : 'bg-red-500'}`}>
                          {paymentResult.stepStatus.payment.success ? (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-800">Payment</p>
                          <p className={`text-xs ${paymentResult.stepStatus.payment.success ? 'text-green-600' : 'text-red-600'}`}>
                            {paymentResult.stepStatus.payment.message}
                          </p>
                        </div>
                      </div>
                      
                      {/* Enrollment Step */}
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${paymentResult.stepStatus.enrollment.success ? 'bg-green-500' : 'bg-yellow-500'}`}>
                          {paymentResult.stepStatus.enrollment.success ? (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-800">Enrollment</p>
                          <p className={`text-xs ${paymentResult.stepStatus.enrollment.success ? 'text-green-600' : 'text-yellow-600'}`}>
                            {paymentResult.stepStatus.enrollment.message}
                          </p>
                        </div>
                      </div>
                      
                      {/* Email Step */}
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${paymentResult.stepStatus.email.success ? 'bg-green-500' : 'bg-yellow-500'}`}>
                          {paymentResult.stepStatus.email.success ? (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-800">Email Confirmation</p>
                          <p className={`text-xs ${paymentResult.stepStatus.email.success ? 'text-green-600' : 'text-yellow-600'}`}>
                            {paymentResult.stepStatus.email.message}
                          </p>
                        </div>
                      </div>
                        </div>
                      </div>
                    )}

                    {/* Transaction Info */}
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="font-bold text-gray-800 text-sm">Transaction Details</h3>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">ID:</span>
                          <span className="text-gray-900 font-semibold">{paymentResult.transactionId}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Method:</span>
                          <span className="text-gray-900 font-semibold">{paymentResult.paymentMethod}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date:</span>
                          <span className="text-gray-900 font-semibold">{paymentResult.transactionDate}</span>
                        </div>
                      </div>
                    </div>

                    {/* Class Info */}
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        <h3 className="font-bold text-gray-800 text-sm">Class Information</h3>
                      </div>
                      <p className="text-gray-900 font-semibold text-base">{paymentResult.className}</p>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    {/* Students */}
                    <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                      <div className="flex items-center gap-2 mb-3">
                        <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        <h3 className="font-bold text-gray-800 text-sm">Enrolled Students ({paymentResult.students?.length || 0})</h3>
                      </div>
                      <div className="max-h-48 overflow-y-auto space-y-2 pr-1" style={{scrollbarWidth: 'thin', scrollbarColor: '#fb923c #fed7aa'}}>
                        {paymentResult.students?.map((student, index) => (
                          <div key={index} className="bg-white rounded px-3 py-2 border border-orange-200 flex items-center gap-2">
                            <span className="text-orange-600 font-bold text-sm">{index + 1}.</span>
                            <p className="text-gray-900 font-semibold text-sm flex-1">
                              {student.student_First_Name || student.Student_First_Name || student.firstName} {student.student_Last_Name || student.Student_Last_Name || student.lastName}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Total Amount */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border-2 border-green-200">
                      <div className="flex justify-between items-center">
                        <span className="text-base font-bold text-gray-800">Total Amount Paid:</span>
                        <span className="text-2xl font-black text-green-600">${paymentResult.totalAmount?.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Receipt Download Button */}
                    {paymentResult.receiptUrl && (
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border-2 border-blue-200">
                        <div className="flex items-center gap-3 mb-3">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <h3 className="font-bold text-gray-800 text-sm">Payment Receipt</h3>
                        </div>
                        <div className="flex gap-2">
                          <a
                            href={paymentResult.receiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View Receipt
                          </a>
                          <a
                            href={paymentResult.receiptUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Button */}
                <div className="flex justify-center pt-2">
                  <button
                    onClick={() => {
                      if (autoCloseTimer) clearTimeout(autoCloseTimer);
                      setShowSuccessModal(false);
                      dispatch(clearEnrollmentData());
                      navigate('/');
                    }}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-3 px-10 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-base flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Go to Home
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
            {/* Modal */}
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col animate-slideInUp">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 text-center rounded-t-2xl flex-shrink-0">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                  <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold mb-1">{isAddressMismatch ? 'Billing Address Mismatch' : 'Payment Failed'}</h2>
                <p className="text-red-100 text-sm">
                  {isAddressMismatch ? 'Your card was not charged' : 'Your payment could not be processed'}
                </p>
              </div>

              {/* Scrollable Content */}
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                {/* Error Message - Scrollable */}
                <div className="bg-red-50 rounded-lg p-4 border-2 border-red-200">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-800 mb-2 text-base">What happened</h3>
                      <div className="bg-white rounded-lg p-3 border border-red-200 max-h-48 overflow-y-auto">
                        <p className="text-sm text-gray-800 leading-relaxed break-words whitespace-pre-wrap">
                          {errorMessage}
                        </p>
                        {errorReferenceCode && (
                          <p className="text-xs text-gray-500 mt-2">
                            Reference code: <span className="font-mono">{errorReferenceCode}</span> — please quote this if you contact us.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Help Text */}
                <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800 mb-2 text-base">What to do next:</h4>
                      <ul className="text-sm text-gray-700 space-y-1.5">
                        {isAddressMismatch ? (
                          <>
                            <li className="flex items-start gap-2">
                              <span className="text-blue-600 font-bold mt-0.5">•</span>
                              <span>Wrong ZIP? Click <strong>Try Again</strong> and correct the <strong>Billing ZIP Code</strong> field on this page</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-blue-600 font-bold mt-0.5">•</span>
                              <span>Wrong street address? Click <strong>Update Address</strong>, enter it exactly as it appears on your card statement, save, then return to this class and pay again</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-blue-600 font-bold mt-0.5">•</span>
                              <span>If your address is already correct, contact your card issuer to confirm the address they have on file</span>
                            </li>
                          </>
                        ) : (
                          <>
                            <li className="flex items-start gap-2">
                              <span className="text-blue-600 font-bold mt-0.5">•</span>
                              <span>Verify your payment information is correct</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-blue-600 font-bold mt-0.5">•</span>
                              <span>Check that you have sufficient funds</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-blue-600 font-bold mt-0.5">•</span>
                              <span>Contact your bank if the issue persists</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-blue-600 font-bold mt-0.5">•</span>
                              <span>Try a different payment method</span>
                            </li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons - Fixed at bottom */}
              <div className="p-6 pt-0 flex-shrink-0">
                <div className="flex gap-3">
                  {isAddressMismatch && (
                    <button
                      onClick={() => {
                        setShowErrorModal(false);
                        navigate(PROFILE_PATH);
                      }}
                      className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                      Update Address
                    </button>
                  )}
                  <button
                    onClick={() => setShowErrorModal(false)}
                    className={`flex-1 font-bold py-3 px-6 rounded-full transition-all duration-300 ${isAddressMismatch
                      ? 'bg-gray-200 hover:bg-gray-300 text-gray-800 hover:shadow-md'
                      : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'}`}
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => {
                      setShowErrorModal(false);
                      navigate('/');
                    }}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-3 px-6 rounded-full transition-all duration-300 hover:shadow-md"
                  >
                    Go to Home
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Payment;
